import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "../api";
import { useI18n, formatPrice } from "../i18n";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const requestId = params.get("request_id");
  const { t } = useI18n();
  const [state, setState] = useState(sessionId ? "checking" : "request");
  const [data, setData] = useState(null);
  const pollRef = useRef({ attempts: 0, timer: null });

  useEffect(() => {
    if (!sessionId) return undefined;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        pollRef.current.attempts += 1;
        if (pollRef.current.attempts >= maxAttempts) {
          setState("failed");
          return;
        }
        pollRef.current.timer = setTimeout(poll, interval);
      } catch {
        pollRef.current.attempts += 1;
        if (pollRef.current.attempts >= maxAttempts) {
          setState("failed");
          return;
        }
        pollRef.current.timer = setTimeout(poll, interval);
      }
    };
    poll();
    return () => {
      if (pollRef.current.timer) clearTimeout(pollRef.current.timer);
    };
  }, [sessionId]);

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="success-page">
      {state === "checking" && (
        <div data-testid="success-checking">
          <Loader2 size={48} className="mx-auto text-[#FFB400] animate-spin" />
          <p className="mt-6 text-zinc-300 font-mono-ui tracking-widest uppercase text-sm">
            {t.success.checking}
          </p>
        </div>
      )}

      {(state === "paid" || state === "request") && (
        <div data-testid="success-confirmed">
          <CheckCircle2 size={64} className="mx-auto text-[#FFB400]" />
          <h1 className="mt-6 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white" data-testid="success-title">
            {t.success.title}
          </h1>
          <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl mx-auto">
            {t.success.sub}
          </p>
          {data && state === "paid" && (
            <div className="mt-10 inline-flex flex-col bg-[#0d0d0d] border border-white/10 p-6 text-left">
              <span className="text-label">{t.common.total}</span>
              <span className="font-heading text-3xl text-white">
                {formatPrice(data.amount_total)}
              </span>
              <span className="text-xs text-zinc-500 mt-2 font-mono-ui uppercase tracking-widest">
                {data.metadata?.car_name} · {data.metadata?.days} days
              </span>
            </div>
          )}
          {requestId && (
            <div className="mt-10 inline-flex flex-col bg-[#0d0d0d] border border-white/10 p-6 text-left">
              <span className="text-label">Reservation #</span>
              <span className="font-mono-ui text-zinc-300 mt-1 text-sm" data-testid="request-id">{requestId}</span>
            </div>
          )}
          <div className="mt-12">
            <Link to="/fleet" className="btn-primary" data-testid="success-cta">
              {t.success.cta}
            </Link>
          </div>
        </div>
      )}

      {(state === "failed" || state === "expired") && (
        <div data-testid="success-error">
          <XCircle size={64} className="mx-auto text-[#FFB400]" />
          <h1 className="mt-6 font-heading text-4xl font-bold tracking-tight text-white">
            {state === "expired" ? t.success.expired : t.success.failed}
          </h1>
          <Link to="/fleet" className="mt-10 btn-outline inline-flex">
            {t.success.cta}
          </Link>
        </div>
      )}
    </div>
  );
};

export default Success;
